import { Card, CardContent, Typography, useTheme } from "@mui/material";
import { DateTime } from "luxon";
import { Chart, ContainerGrid, LineGraphSkeleton, StatisticsCard } from "src/components";
import { useGetDailyPageViewSummary } from "src/http";
import { ILineGraphDataset } from "src/models";

type DateParams = {
  startDateUtc: string;
  endDateUtc: string;
};

interface IProps {
  dateParams: DateParams;
}

const AppUsageSummarySection = (props: IProps) => {
  const theme = useTheme();
  const { data: pageViewSummary, isLoading } = useGetDailyPageViewSummary(props.dateParams);

  const totalPageViews = pageViewSummary?.buckets.reduce((sum, bucket) => sum + bucket.totalPageViews, 0) ?? 0;
  const avgSessionsPerUser =
    pageViewSummary && pageViewSummary.overallUniqueVisitors > 0
      ? (pageViewSummary.overallTotalSessions / pageViewSummary.overallUniqueVisitors).toFixed(1)
      : 0;
  const medianSessionDuration =
    pageViewSummary?.medianSessionDurationMinutes != null
      ? `${pageViewSummary.medianSessionDurationMinutes.toFixed(1)}`
      : "—";
  const avgSessionDuration =
    pageViewSummary?.avgSessionDurationMinutes != null
      ? `${pageViewSummary.avgSessionDurationMinutes.toFixed(1)}`
      : "—";

  const bucketData = pageViewSummary?.buckets.map((bucket) => ({
    x: DateTime.fromISO(bucket.bucketStart).toFormat("MMM d"),
    uniqueVisitors: bucket.uniqueVisitors,
    totalPageViews: bucket.totalPageViews,
    totalSessions: bucket.totalSessions,
  }));

  const charts: { label: string; dataset: ILineGraphDataset }[] = [
    {
      label: "Unique Visitors",
      dataset: {
        title: "Unique Visitors",
        displayAsBar: false,
        color: theme.palette.primary.main,
        dotted: false,
        data: bucketData?.map(({ x, uniqueVisitors }) => ({ x, y: uniqueVisitors })) ?? [],
        sortOrder: "0",
      },
    },
    {
      label: "Page Views",
      dataset: {
        title: "Page Views",
        displayAsBar: false,
        color: theme.palette.primary.main,
        dotted: false,
        data: bucketData?.map(({ x, totalPageViews }) => ({ x, y: totalPageViews })) ?? [],
        sortOrder: "0",
      },
    },
    {
      label: "Sessions",
      dataset: {
        title: "Sessions",
        displayAsBar: false,
        color: theme.palette.primary.main,
        dotted: false,
        data: bucketData?.map(({ x, totalSessions }) => ({ x, y: totalSessions })) ?? [],
        sortOrder: "0",
      },
    },
  ];

  return (
    <>
      <StatisticsCard
        isLoading={isLoading}
        data={[
          { title: "Unique Visitors", value: pageViewSummary?.overallUniqueVisitors ?? 0 },
          { title: "Page Views", value: totalPageViews },
          { title: "Sessions", value: pageViewSummary?.overallTotalSessions ?? 0 },
          { title: "Avg Sessions\nPer User", value: avgSessionsPerUser },
          { title: "Avg Session\nDuration (min)", value: avgSessionDuration },
          { title: "Median Session\nDuration (min)", value: medianSessionDuration },
        ]}
      />
      <ContainerGrid container spacing={1}>
        {charts.map(({ label, dataset }) => (
          <ContainerGrid key={label} size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  {label}
                </Typography>
                {isLoading || !pageViewSummary ? (
                  <LineGraphSkeleton lineCount={1} />
                ) : (
                  <Chart datasets={[dataset]} showXAxis showYAxis minYValue={0} sx={{ height: 200 }} />
                )}
              </CardContent>
            </Card>
          </ContainerGrid>
        ))}
      </ContainerGrid>
    </>
  );
};

export { AppUsageSummarySection };
